import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { ParticipantId } from './ParticipantId';
import { RaceContestant } from './RaceContestant';
import type { EntityState } from './../enums/EntityState';
import type { Gender } from './../enums/Gender';
import { formatDateIso } from '@/utilities/dateutils';

export class ContestantBase extends BaseObj {
    public ID: number;
    public __id: number;

    public readonly Age: number | null;

    public Bib: number | null;
    public __bib: number | null;

    public Birthday: Date | null;
    public __birthday: Date | null;

    public CategoryID: number;
    public __categoryid: number;

    public Chip1: string | null;
    public __chip1: string | null;

    public Chip2: string | null;
    public __chip2: string | null;

    public ClubID: number | null;
    public __clubid: number | null;

    public ContestantStatusID: number;
    public __contestantstatusid: number;

    public readonly EventAge: number | null;

    public EventID: number;
    public __eventid: number;

    public EventTeamId: number | null;
    public __eventteamid: number | null;

    public Firstname: string | null;
    public __firstname: string | null;

    public Gender: Gender | null;
    public __gender: Gender | null;

    public GPSDeviceID: string | null;
    public __gpsdeviceid: string | null;

    public ISonenID: string | null;
    public __isonenid: string | null;

    public NationalityCountryID: number;
    public __nationalitycountryid: number;

    public ParticipantID: number;
    public __participantid: number;

    public ParticipantIDs: ParticipantId[];
    public __participantids: ParticipantId[];

    public RaceContestants: RaceContestant[];
    public __racecontestants: RaceContestant[];

    public RegionID: number | null;
    public __regionid: number | null;

    public RelayTeamName: string | null;
    public __relayteamname: string | null;

    public Surname: string | null;
    public __surname: string | null;

    public TeamID: number | null;
    public __teamid: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Age?: number | null;
        Bib?: number | null;
        Birthday?: Date | null;
        CategoryID?: number;
        Chip1?: string | null;
        Chip2?: string | null;
        ClubID?: number | null;
        ContestantStatusID?: number;
        EntityState?: EntityState;
        EventAge?: number | null;
        EventID?: number;
        EventTeamId?: number | null;
        Firstname?: string | null;
        Gender?: Gender | null;
        GPSDeviceID?: string | null;
        ISonenID?: string | null;
        NationalityCountryID?: number;
        ParticipantID?: number;
        ParticipantIDs?: ParticipantId[];
        RaceContestants?: RaceContestant[];
        RegionID?: number | null;
        RelayTeamName?: string | null;
        Surname?: string | null;
        TeamID?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<ContestantBase>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('ContestantBase tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Age = values.Age ?? null;

        this.Bib = values.Bib ?? null;
        this.__bib = values.Bib ?? null;
        Object.defineProperty(this, 'Bib', {
            get(): number | null {
                return this.__bib;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Bib', this.__bib, value);
                this.__bib = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Birthday = values.Birthday ?? null;
        this.__birthday = values.Birthday ?? null;
        Object.defineProperty(this, 'Birthday', {
            get(): Date | null {
                return this.__birthday;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('Birthday', this.__birthday, value);
                this.__birthday = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CategoryID = values.CategoryID ?? 0;
        this.__categoryid = values.CategoryID ?? 0;
        Object.defineProperty(this, 'CategoryID', {
            get(): number {
                return this.__categoryid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('CategoryID', this.__categoryid, value);
                this.__categoryid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Chip1 = values.Chip1 ?? null;
        this.__chip1 = values.Chip1 ?? null;
        Object.defineProperty(this, 'Chip1', {
            get(): string | null {
                return this.__chip1;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Chip1', this.__chip1, value);
                this.__chip1 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Chip2 = values.Chip2 ?? null;
        this.__chip2 = values.Chip2 ?? null;
        Object.defineProperty(this, 'Chip2', {
            get(): string | null {
                return this.__chip2;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Chip2', this.__chip2, value);
                this.__chip2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ClubID = values.ClubID ?? null;
        this.__clubid = values.ClubID ?? null;
        Object.defineProperty(this, 'ClubID', {
            get(): number | null {
                return this.__clubid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ClubID', this.__clubid, value);
                this.__clubid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContestantStatusID = values.ContestantStatusID ?? 0;
        this.__contestantstatusid = values.ContestantStatusID ?? 0;
        Object.defineProperty(this, 'ContestantStatusID', {
            get(): number {
                return this.__contestantstatusid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ContestantStatusID', this.__contestantstatusid, value);
                this.__contestantstatusid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventAge = values.EventAge ?? null;

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventTeamId = values.EventTeamId ?? null;
        this.__eventteamid = values.EventTeamId ?? null;
        Object.defineProperty(this, 'EventTeamId', {
            get(): number | null {
                return this.__eventteamid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('EventTeamId', this.__eventteamid, value);
                this.__eventteamid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Firstname = values.Firstname ?? null;
        this.__firstname = values.Firstname ?? null;
        Object.defineProperty(this, 'Firstname', {
            get(): string | null {
                return this.__firstname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Firstname', this.__firstname, value);
                this.__firstname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Gender = values.Gender ?? 'm';
        this.__gender = values.Gender ?? 'm';
        Object.defineProperty(this, 'Gender', {
            get(): Gender | null {
                return this.__gender;
            },
            set(value: Gender | null) {
                this.__entityStateHandler.propertyChanged('Gender', this.__gender, value);
                this.__gender = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPSDeviceID = values.GPSDeviceID ?? '';
        this.__gpsdeviceid = values.GPSDeviceID ?? '';
        Object.defineProperty(this, 'GPSDeviceID', {
            get(): string | null {
                return this.__gpsdeviceid;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('GPSDeviceID', this.__gpsdeviceid, value);
                this.__gpsdeviceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ISonenID = values.ISonenID ?? null;
        this.__isonenid = values.ISonenID ?? null;
        Object.defineProperty(this, 'ISonenID', {
            get(): string | null {
                return this.__isonenid;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('ISonenID', this.__isonenid, value);
                this.__isonenid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NationalityCountryID = values.NationalityCountryID ?? 0;
        this.__nationalitycountryid = values.NationalityCountryID ?? 0;
        Object.defineProperty(this, 'NationalityCountryID', {
            get(): number {
                return this.__nationalitycountryid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('NationalityCountryID', this.__nationalitycountryid, value);
                this.__nationalitycountryid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ParticipantID = values.ParticipantID ?? 0;
        this.__participantid = values.ParticipantID ?? 0;
        Object.defineProperty(this, 'ParticipantID', {
            get(): number {
                return this.__participantid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('ParticipantID', this.__participantid, value);
                this.__participantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ParticipantIDs = values.ParticipantIDs ?? [];
        this.__participantids = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.ParticipantIDs ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', newVal),
        });
        this.__participantids.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', this.ParticipantIDs),
            );
        });
        Object.defineProperty(this, 'ParticipantIDs', {
            get(): ParticipantId[] {
                return this.__participantids;
            },
            set(value: ParticipantId[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__participantids = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: ParticipantId[], newVal: ParticipantId[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', newVal),
                });
                this.__participantids.forEach((child: ParticipantId) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', this.ParticipantIDs),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('ParticipantIDs', this.ParticipantIDs);
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceContestants = values.RaceContestants ?? [];
        this.__racecontestants = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.RaceContestants ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceContestants', newVal),
        });
        this.__racecontestants.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceContestants', this.RaceContestants),
            );
        });
        Object.defineProperty(this, 'RaceContestants', {
            get(): RaceContestant[] {
                return this.__racecontestants;
            },
            set(value: RaceContestant[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__racecontestants = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: RaceContestant[], newVal: RaceContestant[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceContestants', newVal),
                });
                this.__racecontestants.forEach((child: RaceContestant) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceContestants', this.RaceContestants),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('RaceContestants', this.RaceContestants);
            },
            enumerable: true,
            configurable: true,
        });

        this.RegionID = values.RegionID ?? null;
        this.__regionid = values.RegionID ?? null;
        Object.defineProperty(this, 'RegionID', {
            get(): number | null {
                return this.__regionid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('RegionID', this.__regionid, value);
                this.__regionid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RelayTeamName = values.RelayTeamName ?? null;
        this.__relayteamname = values.RelayTeamName ?? null;
        Object.defineProperty(this, 'RelayTeamName', {
            get(): string | null {
                return this.__relayteamname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('RelayTeamName', this.__relayteamname, value);
                this.__relayteamname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Surname = values.Surname ?? null;
        this.__surname = values.Surname ?? null;
        Object.defineProperty(this, 'Surname', {
            get(): string | null {
                return this.__surname;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Surname', this.__surname, value);
                this.__surname = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TeamID = values.TeamID ?? null;
        this.__teamid = values.TeamID ?? null;
        Object.defineProperty(this, 'TeamID', {
            get(): number | null {
                return this.__teamid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('TeamID', this.__teamid, value);
                this.__teamid = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Age": ${JSON.stringify(this.Age)}` +
            ',' +
            `"Bib": ${JSON.stringify(this.Bib)}` +
            ',' +
            `"Birthday": ${this.Birthday ? '"' + formatDateIso(this.Birthday).substring(0, 10) + '"' : null}` +
            ',' +
            `"CategoryID": ${JSON.stringify(this.CategoryID)}` +
            ',' +
            `"Chip1": ${JSON.stringify(this.Chip1)}` +
            ',' +
            `"Chip2": ${JSON.stringify(this.Chip2)}` +
            ',' +
            `"ClubID": ${JSON.stringify(this.ClubID)}` +
            ',' +
            `"ContestantStatusID": ${JSON.stringify(this.ContestantStatusID)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventAge": ${JSON.stringify(this.EventAge)}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"EventTeamId": ${JSON.stringify(this.EventTeamId)}` +
            ',' +
            `"Firstname": ${JSON.stringify(this.Firstname)}` +
            ',' +
            `"Gender": ${this.Gender == null ? null : this.Gender == 'm' ? 0 : this.Gender == 'f' ? 1 : this.Gender == 'n' ? 2 : null}` +
            ',' +
            `"GPSDeviceID": ${JSON.stringify(this.GPSDeviceID)}` +
            ',' +
            `"ISonenID": ${JSON.stringify(this.ISonenID)}` +
            ',' +
            `"NationalityCountryID": ${JSON.stringify(this.NationalityCountryID)}` +
            ',' +
            `"ParticipantID": ${JSON.stringify(this.ParticipantID)}` +
            ',' +
            `"ParticipantIDs": [ ${this.ParticipantIDs.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"RaceContestants": [ ${this.RaceContestants.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"RegionID": ${JSON.stringify(this.RegionID)}` +
            ',' +
            `"RelayTeamName": ${JSON.stringify(this.RelayTeamName)}` +
            ',' +
            `"Surname": ${JSON.stringify(this.Surname)}` +
            ',' +
            `"TeamID": ${JSON.stringify(this.TeamID)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of ContestantBase from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Bib?: number | null;
        Birthday?: Date | null;
        CategoryID?: number;
        Chip1?: string | null;
        Chip2?: string | null;
        ClubID?: number | null;
        ContestantStatusID?: number;
        EventID?: number;
        EventTeamId?: number | null;
        Firstname?: string | null;
        Gender?: Gender | null;
        GPSDeviceID?: string | null;
        ISonenID?: string | null;
        NationalityCountryID?: number;
        ParticipantID?: number;
        ParticipantIDs?: ParticipantId[];
        RaceContestants?: RaceContestant[];
        RegionID?: number | null;
        RelayTeamName?: string | null;
        Surname?: string | null;
        TeamID?: number | null;
        EntityState?: 'Added' | 'Unchanged';
    }): ContestantBase {
        return new ContestantBase(values);
    }

    public static _FromServerJson(json: any): ContestantBase {
        return new ContestantBase({
            ID: json.ID,
            Age: json.Age ?? null,
            Bib: json.Bib ?? null,
            Birthday: json.Birthday ? (json.Birthday instanceof Date ? json.Birthday : new Date(json.Birthday + ' 00:00:00')) : null,
            CategoryID: json.CategoryID,
            Chip1: json.Chip1 ?? '',
            Chip2: json.Chip2 ?? '',
            ClubID: json.ClubID ?? null,
            ContestantStatusID: json.ContestantStatusID,
            EventAge: json.EventAge ?? null,
            EventID: json.EventID,
            EventTeamId: json.EventTeamId ?? null,
            Firstname: json.Firstname ?? '',
            Gender: json.Gender == null ? null : json.Gender == 0 ? 'm' : json.Gender == 1 ? 'f' : json.Gender == 2 ? 'n' : null,
            GPSDeviceID: json.GPSDeviceID ?? '',
            ISonenID: json.ISonenID ?? '',
            NationalityCountryID: json.NationalityCountryID,
            ParticipantID: json.ParticipantID,
            ParticipantIDs: json.ParticipantIDs?.map((x: any) => ParticipantId._FromServerJson(x)),
            RaceContestants: json.RaceContestants?.map((x: any) => RaceContestant._FromServerJson(x)),
            RegionID: json.RegionID ?? null,
            RelayTeamName: json.RelayTeamName ?? '',
            Surname: json.Surname ?? '',
            TeamID: json.TeamID ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of ContestantBase from another instance of ContestantBase.
     * @param other The ContestantBase you want to clone
     * @returns A cloned ContestantBase
     */
    public static FromOther(other: ContestantBase): ContestantBase {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return ContestantBase._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return ContestantBase.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'Age'
            | 'Bib'
            | 'Birthday'
            | 'CategoryID'
            | 'Chip1'
            | 'Chip2'
            | 'ClubID'
            | 'ContestantStatusID'
            | 'EventAge'
            | 'EventID'
            | 'EventTeamId'
            | 'Firstname'
            | 'Gender'
            | 'GPSDeviceID'
            | 'ISonenID'
            | 'NationalityCountryID'
            | 'ParticipantID'
            | 'ParticipantIDs'
            | 'RaceContestants'
            | 'RegionID'
            | 'RelayTeamName'
            | 'Surname'
            | 'TeamID'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'ContestantBase';
}
